import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useActor, useSelector } from '@xstate/react';
import { Alert, Box } from '@chargify/custom-ui-components';
import { TokenizationError } from '@chargify/payments-sdk';

import {
  paymentProfileSelectors,
  PaymentProfileInterpreter,
} from '../../machines';

import { Errors } from './types';

export type PaymentProfileErrorContainerProps = {
  /**
   * Payment profile machine reference
   */
  paymentProfileRef: PaymentProfileInterpreter;
};

const CreatePaymentProfileErrorContainer = ({
  paymentProfileRef,
}: PaymentProfileErrorContainerProps) => {
  const { t } = useTranslation(['subscriptions-manager']);
  const [state] = useActor(paymentProfileRef);

  const { tokenization } = useSelector(
    paymentProfileRef,
    paymentProfileSelectors.getPaymentProfileErrors
  );

  const translate = (key: string) =>
    t(key, {
      ns: 'subscriptions-manager',
    });

  const errorRef = useCallback((errorEl: HTMLDivElement) => {
    if (errorEl) {
      console.log(errorEl);
      errorEl.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, []);

  const getTokenizationErrorMessage = useCallback(
    (error: TokenizationError) => {
      const { invalidFields, errors } = error;
      if (invalidFields) return null;

      return Array.isArray(errors) ? errors.join(' ') : errors;
    },
    []
  );

  if (state.matches(Errors.CreateProfile)) {
    return (
      <Box ref={errorRef}>
        <Alert
          color="danger"
          title={translate('payment_profile.create_error_title')}
        >
          <Alert.TextMessage>
            {translate('payment_profile.create_error_message')}
          </Alert.TextMessage>
        </Alert>
      </Box>
    );
  }

  if (state.matches(Errors.ConnectProfileWithSubscription)) {
    return (
      <Box ref={errorRef}>
        <Alert
          color="danger"
          title={translate('payment_profile.connect_profile_error_title')}
        >
          <Alert.TextMessage>
            {translate('payment_profile.connect_profile_error_message')}
          </Alert.TextMessage>
        </Alert>
      </Box>
    );
  }

  if (state.matches(Errors.Tokenization) && tokenization) {
    const message = getTokenizationErrorMessage(tokenization);
    if (message)
      return (
        <Box ref={errorRef} paddingBottom={8}>
          <Alert color="danger">
            <Alert.TextMessage>
              {translate('payment_profile.tokenization_error_message')}
            </Alert.TextMessage>
          </Alert>
        </Box>
      );
  }

  return null;
};

export default CreatePaymentProfileErrorContainer;
